import { useState, useEffect, useCallback, useRef } from 'react';
import binanceService, { MarketData } from '../services/binanceService';
import { useAuth } from '../contexts/AuthContext';
import serviceManager from '../services/serviceManager';

/**
 * React hook for using Binance market data
 * 
 * This hook provides:
 * 1. Initial market data fetch on mount
 * 2. Real-time updates via WebSocket
 * 3. Market data by category
 * 4. Loading and error states
 * 5. Automatic cleanup on unmount
 */
export const useBinanceMarketData = () => {
  const { user } = useAuth();
  const [marketData, setMarketData] = useState<Map<string, MarketData>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  /**
   * Fetch initial market data
   */
  const fetchInitialData = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      setError('User must be authenticated to fetch market data');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const data = await binanceService.fetchInitialMarketData();
      
      setMarketData(data);
      setIsLoading(false);
      
      // Data loaded successfully
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch market data';
      setError(errorMessage);
      setIsLoading(false);
      console.error('Error fetching initial market data:', err);
    }
  }, [user]);

  /**
   * Initialize WebSocket connection
   */
  const initializeWebSocket = useCallback(() => {
    if (!user) {
      return;
    }

    try {
      // WebSocket connection is automatically managed by BinanceService
      
      // Subscribe to updates
      const unsubscribe = binanceService.subscribe((data) => {
        setMarketData(new Map(data));
        setIsConnected(true);
      });
      
      unsubscribeRef.current = unsubscribe;
      
    } catch (err) {
      console.error('Error subscribing to WebSocket:', err);
      setError('Failed to connect to live data stream');
    }
  }, [user]);

  /**
   * Get market data for specific symbols
   */
  const getMarketDataForSymbols = useCallback((symbols: string[]): MarketData[] => {
    return binanceService.getMarketDataForSymbols(symbols);
  }, []);

  /**
   * Get market data by category
   */
  const getMarketDataByCategory = useCallback((category: string): MarketData[] => {
    return binanceService.getMarketDataByCategory(category);
  }, []);

  /**
   * Get market data for a specific symbol
   */
  const getMarketDataForSymbol = useCallback((symbol: string): MarketData | undefined => {
    return marketData.get(symbol);
  }, [marketData]);

  /**
   * Get top gainers (positive price change)
   */
  const getTopGainers = useCallback((limit: number = 10): MarketData[] => {
    return Array.from(marketData.values())
      .filter(item => item.isPositive)
      .sort((a, b) => {
        const aChange = parseFloat(a.change.replace(/[+%]/g, ''));
        const bChange = parseFloat(b.change.replace(/[+%]/g, ''));
        return bChange - aChange;
      })
      .slice(0, limit);
  }, [marketData]);

  /**
   * Get top losers (negative price change)
   */
  const getTopLosers = useCallback((limit: number = 10): MarketData[] => {
    return Array.from(marketData.values())
      .filter(item => !item.isPositive)
      .sort((a, b) => {
        const aChange = parseFloat(a.change.replace(/[-%]/g, ''));
        const bChange = parseFloat(b.change.replace(/[-%]/g, ''));
        return bChange - aChange;
      })
      .slice(0, limit);
  }, [marketData]);

  /**
   * Get highest volume assets
   */
  const getHighestVolume = useCallback((limit: number = 10): MarketData[] => {
    return Array.from(marketData.values())
      .sort((a, b) => {
        const aVolume = parseFloat(a.volume.replace(/[KMB]/g, ''));
        const bVolume = parseFloat(b.volume.replace(/[KMB]/g, ''));
        return bVolume - aVolume;
      })
      .slice(0, limit);
  }, [marketData]);

  /**
   * Refresh market data manually
   */
  const refreshData = useCallback(async () => {
    await fetchInitialData();
  }, [fetchInitialData]);

  /**
   * Reconnect WebSocket
   */
  const reconnect = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    
    setIsConnected(false);
    initializeWebSocket();
  }, [initializeWebSocket]);

  // Handle authentication changes
  useEffect(() => {
    if (user && serviceManager.areServicesRunning()) {
      
      // Fetch initial data
      fetchInitialData();
      
      // Initialize WebSocket
      initializeWebSocket();
    } else if (!user) {
      
      // Clean up subscriptions
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      
      // Reset state
      setMarketData(new Map());
      setIsConnected(false);
      setError(null);
    }
    
    // Cleanup on unmount
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [user, fetchInitialData, initializeWebSocket]);

  // Monitor WebSocket connection status
  useEffect(() => {
    const checkConnection = setInterval(() => {
      const currentData = binanceService.getMarketData();
      if (currentData.size > 0 && !isConnected) {
        setIsConnected(true);
      }
    }, 5000);

    return () => clearInterval(checkConnection);
  }, [isConnected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up any pending subscriptions
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []);

  return {
    // Data
    marketData,
    
    // State
    isLoading,
    error,
    isConnected,
    
    // Methods
    fetchInitialData,
    refreshData,
    reconnect,
    getMarketDataForSymbols,
    getMarketDataByCategory,
    getMarketDataForSymbol,
    getTopGainers,
    getTopLosers,
    getHighestVolume,
    
    // Convenience getters
    cryptoData: getMarketDataByCategory('crypto'),
    allData: Array.from(marketData.values()),
    dataCount: marketData.size,
  };
};

export default useBinanceMarketData;
